/**
 * Lightweight browser fingerprint generator.
 *
 * Collects stable browser/device signals and hashes them into a short
 * hex string. No external libraries required.
 *
 * Signals used (all synchronous, no permissions needed):
 *  - screen resolution & color depth
 *  - timezone
 *  - language(s)
 *  - platform / hardwareConcurrency / deviceMemory
 *  - touch support
 *  - canvas rendering fingerprint
 *  - WebGL renderer string
 */

/**
 * Simple string → 32-bit hex hash (djb2 variant)
 */
function hashString(str) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = '#069';
    ctx.fillText('TapLearnGo 🎓', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('fingerprint', 4, 30);

    return canvas.toDataURL();
  } catch {
    return '';
  }
}

function getWebGLRenderer() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : '';
  } catch {
    return '';
  }
}

/**
 * Generate a browser fingerprint hash.
 * Returns a hex string (e.g. "a3f7c29e01b4d").
 * The result is cached for the page lifecycle.
 */
let _cached = null;

export function generateFingerprint() {
  if (_cached) return _cached;

  const signals = [
    // Screen
    `${window.screen.width}x${window.screen.height}`,
    `${window.screen.colorDepth}`,
    `${window.devicePixelRatio || 1}`,

    // Timezone
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    `${new Date().getTimezoneOffset()}`,

    // Language
    navigator.language || '',
    (navigator.languages || []).join(','),

    // Platform & hardware
    navigator.platform || '',
    `${navigator.hardwareConcurrency || 0}`,
    `${navigator.deviceMemory || 0}`,
    `${navigator.maxTouchPoints || 0}`,

    // Canvas
    getCanvasFingerprint(),

    // WebGL
    getWebGLRenderer(),
  ];

  _cached = hashString(signals.join('|||'));
  return _cached;
}
